// src/modules/competitions/pages/CategoryImagesView.tsx

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Card from '../../../shared/components/ui/Card';
import Button from '../../../shared/components/ui/Button';
import Loading from '../../../shared/components/Loading';
import ErrorMessage from '../../../shared/components/ErrorMessage';
import ImageTypeDetailModal from '../components/ImageTypeDetailModal';
import {
    CompetitionImageType,
    CompetitionSubmission,
    ExampleImage
} from '../types';
import { imageTypesService } from '../services/imageTypesService';
import { competitionService } from '../services/competitionService';
import { useAppContext } from '../../../shared/context/AppContext';

import VideoTypeDetailModal from '../components/VideoTypeDetailModal';
import {
    CompetitionVideoType,
    CompetitionVideoSubmission,
    VideoTypeWithExample
} from '../types';
import { videoTypesService } from '../services/videoTypesService';

import '../styles/categoryImages.css';

// Interfaz para almacenar imageType con su imagen ejemplo
interface ImageTypeWithExample extends CompetitionImageType {
    exampleImage?: ExampleImage;
}

const CategoryImagesView: React.FC = () => {
    const { categoryId } = useParams<{ categoryId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const { state: appState } = useAppContext();
    const { auth } = appState;

    const locationState = location.state as {
        categoryName?: string;
        level?: string;
        participantId?: number;
        competitionId?: number;
    } | null;

    const [categoryRules, setCategoryRules] = useState<string | null>(null);
    const [imageTypes, setImageTypes] = useState<ImageTypeWithExample[]>([]);
    const [submissions, setSubmissions] = useState<CompetitionSubmission[]>([]);
    const [selectedImageType, setSelectedImageType] = useState<CompetitionImageType | null>(null);
    const [hoveredCardId, setHoveredCardId] = useState<number | null>(null);
    const [mobileActiveCardId, setMobileActiveCardId] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [progress, setProgress] = useState({ uploaded: 0, total: 0, percentage: 0 });
    // 🆕 AGREGAR después de los estados existentes de imágenes

    // Estados para videos
    const [videoTypes, setVideoTypes] = useState<VideoTypeWithExample[]>([]);
    const [videoSubmissions, setVideoSubmissions] = useState<CompetitionVideoSubmission[]>([]);
    const [selectedVideoType, setSelectedVideoType] = useState<CompetitionVideoType | null>(null);
    const [hoveredVideoCardId, setHoveredVideoCardId] = useState<number | null>(null);
    const [mobileActiveVideoCardId, setMobileActiveVideoCardId] = useState<number | null>(null);
    const [videoProgress, setVideoProgress] = useState({ uploaded: 0, total: 0, percentage: 0 });


    useEffect(() => {
    if (categoryId && locationState?.level) {
        loadData(); // ✅ Carga imágenes Y videos
    } else {
        setError('Faltan parámetros necesarios');
        setIsLoading(false);
    }
}, [categoryId, locationState?.level]);

    const loadData = async () => {
        setIsLoading(true);
        try {
            await Promise.all([
                loadImageTypes(),
                loadVideoTypes()
            ]);
        } catch (err) {
            setError('Error al cargar datos');
        } finally {
            setIsLoading(false);
        }
    };
    // 🔄 REEMPLAZAR la función loadImageTypes existente con esta:

    const loadImageTypes = async () => {
        try {
            // No llamar setIsLoading aquí, se maneja en loadData()

            // 1. Cargar detalles de la competencia para obtener el reglamento
            if (locationState?.competitionId && categoryId) {
                const compResponse = await competitionService.getCompetitionDetails(
                    locationState.competitionId
                );

                if (compResponse.success && compResponse.data) {
                    const category = compResponse.data.categories.find(
                        c => c.id === parseInt(categoryId)
                    );
                    if (category?.competition_rules) {
                        setCategoryRules(category.competition_rules);
                    }
                }
            }

            // 2. Cargar tipos de imagen
            const response = await imageTypesService.getImageTypesByCategory(
                parseInt(categoryId!),
                locationState!.level!
            );

            if (response.success && response.data) {
                const types = response.data.image_types;

                // 3. Cargar imagen ejemplo de cada tipo
                const typesWithExamples = await Promise.all(
                    types.map(async (imageType) => {
                        try {
                            const examplesRes = await imageTypesService.getExampleImages(imageType.id);
                            if (examplesRes.success && examplesRes.data && examplesRes.data.examples.length > 0) {
                                return {
                                    ...imageType,
                                    exampleImage: examplesRes.data.examples[0]
                                };
                            }
                        } catch (err) {
                            console.error(`Error cargando ejemplo para imageType ${imageType.id}:`, err);
                        }
                        return imageType;
                    })
                );

                setImageTypes(typesWithExamples);

                // 4. Cargar submissions si hay participantId
                if (locationState?.participantId) {
                    await loadSubmissions(typesWithExamples);
                }
            } else {
                setError(response.message || 'Error al cargar tipos de imagen');
            }
        } catch (err) {
            console.error('Error al cargar tipos de imagen:', err);
            throw err; // 🆕 Propagar error para manejarlo en loadData
        }
    };

    // 🆕 AGREGAR estas funciones después de las funciones de imágenes

    const loadVideoTypes = async () => {
        try {
            if (!categoryId || !locationState?.level) return;

            // Cargar tipos de video
            const response = await videoTypesService.getVideoTypesByCategory(
                parseInt(categoryId),
                locationState.level
            );

            if (response.success && response.data) {
                const types = response.data.video_types;

                // Cargar video ejemplo de cada tipo
                const typesWithExamples = await Promise.all(
                    types.map(async (videoType) => {
                        try {
                            const examplesRes = await videoTypesService.getExampleVideos(videoType.id);
                            if (examplesRes.success && examplesRes.data && examplesRes.data.examples.length > 0) {
                                return {
                                    ...videoType,
                                    exampleVideo: examplesRes.data.examples[0]
                                };
                            }
                        } catch (err) {
                            console.error(`Error cargando ejemplo para videoType ${videoType.id}:`, err);
                        }
                        return videoType;
                    })
                );

                setVideoTypes(typesWithExamples);

                // Cargar submissions si hay participantId
                if (locationState?.participantId) {
                    await loadVideoSubmissions(typesWithExamples);
                }
            }
        } catch (err) {
            console.error('Error al cargar tipos de video:', err);
            throw err;
        }
    };

    const loadVideoSubmissions = async (types: CompetitionVideoType[]) => {
        try {
            if (!locationState?.participantId) {
                console.warn('No hay participantId, no se pueden cargar video submissions');
                return;
            }

            const response = await competitionService.getParticipantStatus(
                undefined,
                locationState.participantId
            );

            if (response.success && response.data) {
                // Aquí necesitamos que el backend devuelva submitted_videos
                // Por ahora, creamos un array vacío y lo poblaremos cuando el endpoint esté listo
                const submittedVideos = response.data.submitted_videos || [];
                const subs = Array.isArray(submittedVideos) ? submittedVideos : [];
                setVideoSubmissions(subs);
                calculateVideoProgress(types, subs);
            }
        } catch (err) {
            console.error('Error loading video submissions:', err);
            setVideoSubmissions([]);
        }
    };

    const calculateVideoProgress = (types: CompetitionVideoType[], subs: CompetitionVideoSubmission[]) => {
        const total = types.filter(t => t.is_required).length;
        const uploaded = subs.length;
        const percentage = total > 0 ? Math.round((uploaded / total) * 100) : 0;

        setVideoProgress({ uploaded, total, percentage });
    };

    const getSubmissionForVideoType = (videoTypeId: number): CompetitionVideoSubmission | undefined => {
        if (!videoSubmissions || !Array.isArray(videoSubmissions)) {
            return undefined;
        }
        return videoSubmissions.find(s => s.video_type_id === videoTypeId);
    };

    const handleVideoClick = (videoType: CompetitionVideoType) => {
        const isMobile = window.innerWidth <= 768;

        if (isMobile) {
            if (mobileActiveVideoCardId === videoType.id) {
                setSelectedVideoType(videoType);
                setMobileActiveVideoCardId(null);
            } else {
                setMobileActiveVideoCardId(videoType.id);
            }
        } else {
            setSelectedVideoType(videoType);
        }
    };

    const handleCloseVideoModal = () => {
        setSelectedVideoType(null);
    };

    const handleVideoUploadSuccess = async () => {
        await loadData(); // Recargar todo
    };

    const loadSubmissions = async (types: CompetitionImageType[]) => {
        try {
            // ✅ CORREGIDO: Usar participantId en lugar de competitionId
            if (!locationState?.participantId) {
                console.warn('No hay participantId, no se pueden cargar submissions');
                return;
            }

            const response = await competitionService.getParticipantStatus(
                undefined, // No necesitamos competitionId
                locationState.participantId // ✅ Usar el participant_id específico
            );

            if (response.success && response.data) {
                const submittedImages = response.data.submitted_images || [];
                const subs = Array.isArray(submittedImages) ? submittedImages : [];
                setSubmissions(subs);
                calculateProgress(types, subs);
            }
        } catch (err) {
            console.error('Error loading submissions:', err);
            setSubmissions([]);
        }
    };

    const calculateProgress = (types: CompetitionImageType[], subs: CompetitionSubmission[]) => {
        const total = types.filter(t => t.is_required).length;
        const uploaded = subs.length;
        const percentage = total > 0 ? Math.round((uploaded / total) * 100) : 0;

        setProgress({ uploaded, total, percentage });
    };

    const getSubmissionForImageType = (imageTypeId: number): CompetitionSubmission | undefined => {
        if (!submissions || !Array.isArray(submissions)) {
            return undefined;
        }
        return submissions.find(s => s.image_type_id === imageTypeId);
    };

    const handleImageClick = (imageType: CompetitionImageType) => {
        // En móvil: primer click activa overlay, segundo click abre modal
        const isMobile = window.innerWidth <= 768;

        if (isMobile) {
            if (mobileActiveCardId === imageType.id) {
                // Segundo click: abrir modal
                setSelectedImageType(imageType);
                setMobileActiveCardId(null);
            } else {
                // Primer click: mostrar overlay
                setMobileActiveCardId(imageType.id);
            }
        } else {
            // Desktop: click directo abre modal
            setSelectedImageType(imageType);
        }
    };

    const handleCloseModal = () => {
        setSelectedImageType(null);
    };

    const handleUploadSuccess = async () => {
        await loadImageTypes();
    };

    const handleBack = () => {
        if (locationState?.competitionId) {
            navigate(`/competitions/${locationState.competitionId}`);
        } else {
            navigate('/competitions');
        }
    };

    if (isLoading) {
        return <Loading message="Cargando imágenes requeridas..." />;
    }

    if (error) {
        return (
            <div className="category-images-error">
                <ErrorMessage message={error} type="error" />
                <Button className="btn-primary" onClick={handleBack}>
                    Volver
                </Button>
            </div>
        );
    }

    return (
        <div className="category-images-view">
            {/* Header */}
            <div className="category-header">
                <Button className="back-btn" onClick={handleBack}>
                    ← Volver
                </Button>

                <div className="header-info">
                    <h1>{locationState?.categoryName || 'Categoría'}</h1>
                    <div className="header-meta">
                        <span className="level-badge">{locationState?.level}</span>
                        {locationState?.participantId && (
                            <span className="participant-badge">
                                Participante #{locationState.participantId}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Progress bar */}
            {locationState?.participantId && (
                <Card className="progress-card" variant="elevated">
                    <div className="progress-info">
                        <h3>Progreso de carga de imágenes</h3>
                        <div className="progress-stats">
                            <span className="progress-numbers">
                                <strong>{progress.uploaded}</strong> / {progress.total} imágenes
                            </span>
                            <span className="progress-percentage">{progress.percentage}%</span>
                        </div>
                    </div>
                    <div className="progress-bar">
                        <div
                            className="progress-fill"
                            style={{ width: `${progress.percentage}%` }}
                        />
                    </div>
                </Card>
            )}

            {categoryRules && (
                <Card className="rules-card" variant="elevated">
                    <div className="rules-header">
                        <h3>📋 Reglamento de la Categoría</h3>
                    </div>
                    <div className="rules-content">
                        <div dangerouslySetInnerHTML={{ __html: categoryRules }} />
                    </div>
                </Card>
            )}

            {/* Instrucciones */}

            <Card className="instructions-card" variant="flat">
                <div className="instructions-content">
                    <h3>📸 Imágenes y 🎥 Videos requeridos</h3>
                    <p>
                        Haz clic en cualquier imagen o video para ver sus detalles, criterios de evaluación e
                        instrucciones específicas.
                        {locationState?.participantId && ' Podrás subir tus archivos desde allí.'}
                    </p>
                </div>
            </Card>

            {/* Grid de imágenes */}
            <div className="images-grid">
                {imageTypes.map((imageType, index) => {
                    const submission = getSubmissionForImageType(imageType.id);
                    const isUploaded = !!submission;
                    const showOverlay = hoveredCardId === imageType.id || mobileActiveCardId === imageType.id;

                    // Determinar URL de imagen
                    let imageUrl = '';
                    if (submission) {
                        imageUrl = `/api/api/v1/competitions/get-image.php?id=${submission.id}`;
                    } else if (imageType.exampleImage) {
                        imageUrl = imageType.exampleImage.image_url;
                    }

                    return (
                        <div
                            key={imageType.id}
                            className={`image-card ${isUploaded ? 'uploaded' : 'pending'} ${showOverlay ? 'show-overlay' : ''}`}
                            onClick={() => handleImageClick(imageType)}
                            onMouseEnter={() => setHoveredCardId(imageType.id)}
                            onMouseLeave={() => setHoveredCardId(null)}
                        >
                            {/* Imagen de fondo */}
                            {imageUrl ? (
                                <div
                                    className="card-background"
                                    style={{ backgroundImage: `url(${imageUrl})` }}
                                />
                            ) : (
                                <div className="card-background placeholder-bg">
                                    <div className="placeholder-icon">📷</div>
                                </div>
                            )}

                            {/* Status badge - Siempre visible */}
                            <div className={`status-badge ${isUploaded ? 'uploaded-badge' : 'example-badge'}`}>
                                {isUploaded ? 'Subida' : 'Ejemplo'}
                            </div>

                            {/* Número de orden - Siempre visible */}
                            <div className="image-number">{index + 1}</div>

                            {/* Overlay con información - Se muestra al hacer hover o click móvil */}
                            <div className="card-overlay">
                                <div className="overlay-content">
                                    <h4 className="overlay-title">
                                        {imageType.name}
                                        {imageType.is_required && (
                                            <span className="required-badge">*</span>
                                        )}
                                    </h4>

                                    {imageType.description && (
                                        <p className="overlay-description">
                                            {imageType.description}
                                        </p>
                                    )}

                                    <div className="overlay-specs">
                                        <div className="spec-row">
                                            <span className="spec-label">Formatos:</span>
                                            <span className="spec-value">
                                                {imageType.allowed_formats.map(f => f.toUpperCase()).join(', ')}
                                            </span>
                                        </div>
                                        <div className="spec-row">
                                            <span className="spec-label">Tamaño máx:</span>
                                            <span className="spec-value">{imageType.max_file_size_mb}MB</span>
                                        </div>
                                    </div>

                                    <div className="overlay-action">
                                        <span>👁️ Ver detalles completos</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>


{/* Sección de Videos */}
{videoTypes.length > 0 && (
    <>
        {/* Divisor de sección */}
        <div className="section-divider">
            <div className="divider-line"></div>
            <h2 className="section-title">🎥 VIDEOS REQUERIDOS</h2>
            <div className="divider-line"></div>
        </div>

        {/* Progress bar de videos */}
        {locationState?.participantId && videoProgress.total > 0 && (
            <Card className="progress-card" variant="elevated">
                <div className="progress-info">
                    <h3>Progreso de carga de videos</h3>
                    <div className="progress-stats">
                        <span className="progress-numbers">
                            <strong>{videoProgress.uploaded}</strong> / {videoProgress.total} videos
                        </span>
                        <span className="progress-percentage">{videoProgress.percentage}%</span>
                    </div>
                </div>
                <div className="progress-bar">
                    <div
                        className="progress-fill"
                        style={{ width: `${videoProgress.percentage}%` }}
                    />
                </div>
            </Card>
        )}

        {/* Grid de videos */}
        <div className="videos-grid">
            {videoTypes.map((videoType, index) => {
                const submission = getSubmissionForVideoType(videoType.id);
                const isUploaded = !!submission;
                const showOverlay = hoveredVideoCardId === videoType.id || mobileActiveVideoCardId === videoType.id;

                // Determinar URL de video
                let videoUrl = '';
                if (submission && locationState?.participantId) {
                    videoUrl = videoTypesService.getParticipantVideoUrl(
                        locationState.participantId,
                        videoType.id
                    );
                } else if (videoType.exampleVideo) {
                    videoUrl = videoType.exampleVideo.video_url;
                }

                return (
                    <div
                        key={videoType.id}
                        className={`video-card ${isUploaded ? 'uploaded' : 'pending'} ${showOverlay ? 'show-overlay' : ''}`}
                        onClick={() => handleVideoClick(videoType)}
                        onMouseEnter={() => setHoveredVideoCardId(videoType.id)}
                        onMouseLeave={() => setHoveredVideoCardId(null)}
                    >
                        {/* Video de fondo (thumbnail o poster) */}
                        {videoUrl ? (
                            <div className="card-background video-background">
                                <video
                                    src={videoUrl}
                                    className="background-video"
                                    muted
                                    playsInline
                                />
                                <div className="video-play-icon">▶️</div>
                            </div>
                        ) : (
                            <div className="card-background placeholder-bg">
                                <div className="placeholder-icon">🎬</div>
                            </div>
                        )}

                        {/* Status badge */}
                        <div className={`status-badge ${isUploaded ? 'uploaded-badge' : 'example-badge'}`}>
                            {isUploaded ? 'Subido' : 'Ejemplo'}
                        </div>

                        {/* Número de orden */}
                        <div className="video-number">{imageTypes.length + index + 1}</div>

                        {/* Overlay con información */}
                        <div className="card-overlay">
                            <div className="overlay-content">
                                <h4 className="overlay-title">
                                    {videoType.name}
                                    {videoType.is_required && (
                                        <span className="required-badge">*</span>
                                    )}
                                </h4>

                                {videoType.description && (
                                    <p className="overlay-description">
                                        {videoType.description}
                                    </p>
                                )}

                                <div className="overlay-specs">
                                    <div className="spec-row">
                                        <span className="spec-label">Formatos:</span>
                                        <span className="spec-value">
                                            {videoType.allowed_formats.map(f => f.toUpperCase()).join(', ')}
                                        </span>
                                    </div>
                                    <div className="spec-row">
                                        <span className="spec-label">Tamaño máx:</span>
                                        <span className="spec-value">{videoType.max_file_size_mb}MB</span>
                                    </div>
                                    <div className="spec-row">
                                        <span className="spec-label">Duración máx:</span>
                                        <span className="spec-value">{videoType.max_duration_seconds}s</span>
                                    </div>
                                </div>

                                <div className="overlay-action">
                                    <span>👁️ Ver detalles completos</span>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    </>
)}

            {/* Summary footer */}
            <Card className="summary-card" variant="flat">
                <div className="summary-content">
                    <div className="summary-item">
                        <span className="summary-label">Total de imágenes:</span>
                        <span className="summary-value">{imageTypes.length}</span>
                    </div>
                    <div className="summary-item">
                        <span className="summary-label">Obligatorias:</span>
                        <span className="summary-value">
                            {imageTypes.filter(t => t.is_required).length}
                        </span>
                    </div>
                    <div className="summary-item">
                        <span className="summary-label">Opcionales:</span>
                        <span className="summary-value">
                            {imageTypes.filter(t => !t.is_required).length}
                        </span>
                    </div>
                </div>
            </Card>

{/* Modal de detalle de videos */}
{selectedVideoType && (
    <VideoTypeDetailModal
        videoType={selectedVideoType}
        participantId={locationState?.participantId}
        existingSubmission={getSubmissionForVideoType(selectedVideoType.id)}
        categoryRules={categoryRules}
        onClose={handleCloseVideoModal}
        onUploadSuccess={handleVideoUploadSuccess}
    />
)}

            {/* Modal de detalle */}
            {selectedImageType && (
                <ImageTypeDetailModal
                    imageType={selectedImageType}
                    participantId={locationState?.participantId}
                    existingSubmission={getSubmissionForImageType(selectedImageType.id)}
                    categoryRules={categoryRules}  // ✅ AGREGADO
                    onClose={handleCloseModal}
                    onUploadSuccess={handleUploadSuccess}
                />
            )}
        </div>
    );
};

export default CategoryImagesView;